import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toPractitionerObject = (dbPractitioner) => ({
  id: dbPractitioner?.id,
  slug: dbPractitioner?.slug,
  practiceName: dbPractitioner?.practice_name,
  practitionerTitle: dbPractitioner?.practitioner_title,
  practitionerName: dbPractitioner?.practitioner_name,
  email: dbPractitioner?.email,
  phone: dbPractitioner?.phone,
  address: dbPractitioner?.address,
  city: dbPractitioner?.city,
  postalCode: dbPractitioner?.postal_code,
  primaryColor: dbPractitioner?.primary_color,
  secondaryColor: dbPractitioner?.secondary_color,
  logoUrl: dbPractitioner?.logo_url,
  logoInitials: dbPractitioner?.logo_initials,
  logoPosition: dbPractitioner?.logo_position,
  logoSize: dbPractitioner?.logo_size,
  signatureUrl: dbPractitioner?.signature_url,
  status: dbPractitioner?.status,
  referralEmail: dbPractitioner?.referral_email,
  createdAt: dbPractitioner?.created_at,
  updatedAt: dbPractitioner?.updated_at
});

// Convert camelCase to snake_case
const toDbObject = (practitioner) => {
  const dbObject = {
    practice_name: practitioner?.practiceName,
    practitioner_title: practitioner?.practitionerTitle,
    practitioner_name: practitioner?.practitionerName,
    email: practitioner?.email,
    phone: practitioner?.phone,
    address: practitioner?.address,
    city: practitioner?.city,
    postal_code: practitioner?.postalCode,
    slug: practitioner?.slug,
    primary_color: practitioner?.primaryColor,
    secondary_color: practitioner?.secondaryColor,
    logo_url: practitioner?.logoUrl,
    logo_initials: practitioner?.logoInitials,
    logo_position: practitioner?.logoPosition,
    logo_size: practitioner?.logoSize,
    signature_url: practitioner?.signatureUrl,
    status: practitioner?.status,
    referral_email: practitioner?.referralEmail
  };

  // Remove undefined values
  Object.keys(dbObject)?.forEach(key => {
    if (dbObject?.[key] === undefined) {
      delete dbObject?.[key];
    }
  });

  return dbObject;
};

export const practitionerService = {
  // Get all practitioners (super admin only)
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('practitioners')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data?.map(toPractitionerObject) || [];
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      throw error;
    }
  },

  // Get practitioner by ID
  async getById(practitionerId) {
    try {
      const { data, error } = await supabase
        ?.from('practitioners')
        ?.select('*')
        ?.eq('id', practitionerId)
        ?.single();

      if (error) throw error;
      return toPractitionerObject(data);
    } catch (error) {
      console.error('Error fetching practitioner:', error);
      throw error;
    }
  },

  // Get practitioner by slug (public access)
  async getBySlug(slug) {
    try {
      const { data, error } = await supabase
        ?.from('practitioners')
        ?.select('*')
        ?.eq('slug', slug)
        ?.eq('status', 'active')
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }
      return toPractitionerObject(data);
    } catch (error) {
      console.error('Error fetching practitioner by slug:', error);
      return null;
    }
  },

  // Create new practitioner
  async create(practitionerData) {
    try {
      const dbData = toDbObject(practitionerData);
      
      const { data, error } = await supabase
        ?.from('practitioners')
        ?.insert(dbData)
        ?.select()
        ?.single();

      if (error) throw error;
      return toPractitionerObject(data);
    } catch (error) {
      console.error('Error creating practitioner:', error);
      throw error;
    }
  },

  // Update practitioner profile
  async update(practitionerId, updates) {
    try {
      const dbUpdates = toDbObject(updates);
      
      const { data, error } = await supabase
        ?.from('practitioners')
        ?.update(dbUpdates)
        ?.eq('id', practitionerId)
        ?.select()
        ?.single();

      if (error) throw error;
      return toPractitionerObject(data);
    } catch (error) {
      console.error('Error updating practitioner:', error);
      throw error;
    }
  },

  // Delete practitioner
  async delete(practitionerId) {
    try {
      const { error } = await supabase
        ?.from('practitioners')
        ?.delete()
        ?.eq('id', practitionerId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting practitioner:', error);
      throw error;
    }
  },

  // Create user account for practitioner via Edge Function
  async createUserAccount(email, password, fullName, practitionerId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/create-practitioner-user`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            full_name: fullName,
            practitioner_id: practitionerId
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de la création du compte');
      }

      return result;
    } catch (error) {
      console.error('Error creating user account:', error);
      throw error;
    }
  },

  // Send credentials email via Resend
  async sendCredentialsEmail(practitionerId, email, password, practitionerName, publicUrl, dashboardUrl) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-credentials-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            practitioner_id: practitionerId,
            email,
            password,
            practitioner_name: practitionerName,
            public_url: publicUrl,
            dashboard_url: dashboardUrl
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }

      return result;
    } catch (error) {
      console.error('Error sending credentials email:', error);
      throw error;
    }
  },

  // Get public referral URL
  getPublicUrl(slug) {
    return `${window.location?.origin}/p/${slug}`;
  },

  // Get QR Code URL for practitioner referral link
  getQRCodeUrl(slug) {
    const publicUrl = this.getPublicUrl(slug);
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(publicUrl)}`;
  },

  // Check slug availability
  async checkSlugAvailability(slug) {
    try {
      const { data, error } = await supabase?.from('practitioners')?.select('id')?.eq('slug', slug)?.single();

      if (error && error?.code === 'PGRST116') {
        return true; // Slug available
      }
      return false; // Slug taken
    } catch (error) {
      console.error('Error checking slug availability:', error);
      throw error;
    }
  }
};